// Globals
import debounce from 'lodash.debounce';
import { ChangeEvent, memo, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { changeQuery, getSubscriptions, selectQuery } from '../../feature';

// Components
import { Input } from '@/ui';
import { RangePicker } from '@/components';
import { StyledFilterRow } from './SubscriptionsFilterContainerStyles';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';

// Helpers
import { SubscriptionsDTO } from '../../dtos';
import { SubscriptionsFiltersFields } from '../../helpers/types';
import { DateFormats } from '@/constants/dates';
import { formatDateForApiRequest } from '@/utils';

type ISubscriptionsFiltersContainerProps = {
  children?: ReactNode;
  className?: string;
};

const SubscriptionsFiltersContainer = ({ children, className }: ISubscriptionsFiltersContainerProps) => {
  const { t } = useTranslation(['subscriptions']);
  const dispatch = useAppDispatch();
  const query = useAppState(selectQuery);
  const { canManage } = usePermissions();
  // The trailing actions column only exists when the admin can manage subscriptions.
  const canManageSubscriptions = canManage(Resource.SUBSCRIPTION);

  const handleDebounceSearch = useMemo(
    () =>
      debounce(async (searchParams) => {
        const dto = new SubscriptionsDTO(searchParams);
        dispatch(getSubscriptions(dto));
      }, 500),
    [dispatch],
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newQuery = { ...query, [name]: value || undefined };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  const handleDateRangeChange = (_: unknown, dateStrings: [string, string]) => {
    const newQuery = {
      ...query,
      [SubscriptionsFiltersFields.DATE_FROM]:
        formatDateForApiRequest(dateStrings[0], DateFormats.DATE_WITHOUT_HOURS) || undefined,
      [SubscriptionsFiltersFields.DATE_TO]:
        formatDateForApiRequest(dateStrings[1], DateFormats.DATE_WITHOUT_HOURS) || undefined,
    };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  return (
    <thead className={className}>
      {children}
      <StyledFilterRow>
        <th>
          <Input
            placeholder={t('subscriptions_search')}
            name={SubscriptionsFiltersFields.EMAIL}
            onChange={handleSearch}
            size="large"
            type="email"
            allowClear
          />
        </th>
        <th>
          <RangePicker
            format={DateFormats.DATE_WITHOUT_HOURS}
            placeholder={[t('subscriptions_range_picker_from'), t('subscriptions_range_picker_to')]}
            onChange={handleDateRangeChange}
            size="middle"
          />
        </th>
        {canManageSubscriptions && <th />}
      </StyledFilterRow>
    </thead>
  );
};

export default memo(SubscriptionsFiltersContainer);
